import Flickity from 'flickity'
import debounce from 'lodash.debounce'
import { parseJSON } from '@/components/helpers/utils'
import { shouldHideCarouselArrows } from '@/components/helpers/carousel'
import { applyHorizontalDragAxis } from '@/components/helpers/flickity-drag-axis'

// Vertical swipes must scroll the page, not drag the carousel.
applyHorizontalDragAxis()

const defaultOptions = {
  contain: true
}
export class Carousel extends HTMLElement {
  /**
   * Flickity translates its `.flickity-slider` track, which promotes the WHOLE track to a
   * compositor layer — not just the visible window. A PDP carried five of them; the
   * 8-cell recommendations track alone measured 2932x597, about 60MB of backing store at
   * DPR 3, with roughly 156MB across all five.
   *
   * Ground truth from Safari Web Inspector on a tethered iPhone: 135 layers / 341MB of
   * layer memory against only ~66MB of decoded images. Carousels were the largest
   * remaining block we control, and the last reproducible crash was "scroll down, then
   * use the recommendations carousel".
   *
   * So Flickity is now initialised only when the carousel is near the viewport and torn
   * down once it is well outside, keeping one or two tracks promoted instead of five.
   * Two observers rather than one to give hysteresis — a single threshold thrashes when
   * the shopper parks right on the boundary.
   */
  static INIT_MARGIN = '600px'
  static TEARDOWN_MARGIN = '1500px'

  connectedCallback() {
    this.setupMutationObserver()
    this.handleResize = debounce(() => this.updateArrowsVisibility(), 100)
    window.addEventListener('resize', this.handleResize)
    this.setupViewportGate()
  }

  setupViewportGate() {
    if (!('IntersectionObserver' in window)) {
      // Fail open: without a gate, behave exactly as before.
      this.initializeCarousel()
      return
    }

    this.initObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return
        this.isInViewRange = true
        this.initializeCarousel()
      },
      { rootMargin: Carousel.INIT_MARGIN }
    )
    this.initObserver.observe(this)

    this.teardownObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) return
        this.isInViewRange = false
        this.teardownCarousel()
      },
      { rootMargin: Carousel.TEARDOWN_MARGIN }
    )
    this.teardownObserver.observe(this)
  }

  /** Releases the Flickity instance (and its layer) while the carousel is far offscreen. */
  teardownCarousel() {
    if (!this.flickity || this.isReinitializing) return

    // Hold the space open. Without this the element collapses when the cells are
    // un-wrapped, the page shortens under the shopper, and scroll position jumps.
    const height = this.getBoundingClientRect().height
    if (height > 0) this.style.minHeight = `${height}px`

    // Same reasoning as reinitializeCarousel: destroy() re-appends every cell to this
    // element, and with the observer attached that reads as an external content change
    // and schedules a reinit. Disconnecting first discards those records.
    this.mutationObserver?.disconnect()
    this.flickity.destroy()
    this.flickity = null

    this.teardownFrame = requestAnimationFrame(() => {
      this.teardownFrame = null
      this.observeChildren()
    })
  }

  initializeCarousel() {
    // Re-entry guard: connectedCallback fires again whenever this element is
    // moved in the DOM, and Flickity would otherwise leak the previous instance.
    if (this.flickity) return

    // Respect the viewport gate. Without this the gate is bypassed by two paths that
    // call `initializeCarousel` directly: the MutationObserver reinit (Athos rendering
    // recommendation results into a carousel that is still far below the fold) and the
    // rAF inside `reinitializeCarousel`, which could resurrect a carousel that was torn
    // down after the reinit was scheduled. Defaults to true so the no-IntersectionObserver
    // path and the pre-observer window still initialise normally.
    if (this.isInViewRange === false) return

    const computedHeight = this.getBoundingClientRect().height

    if (computedHeight > 0) {
      this.style.minHeight = `${computedHeight}px`
    }

    const options = this.carouselSettings
    options.cellSelector = '&:not(style)'
    const mergedOptions = { ...defaultOptions, ...options }
    if (mergedOptions.wrapAround === true) {
      delete mergedOptions.contain
    }
    if (mergedOptions.autoPlay && mergedOptions.freeScroll === true) {
      mergedOptions.freeScroll = false
    }
    this.flickity = new Flickity(this, mergedOptions)

    this.flickity.on('ready', () => {
      this.updateArrowsVisibility()
      requestAnimationFrame(() => {
        const viewport = this.querySelector('.flickity-viewport')
        if (viewport && viewport.style.height) {
          requestAnimationFrame(() => {
            if (this.style.minHeight) {
              this.style.minHeight = ''
            }
          })
        }
      })
    })
  }

  updateArrowsVisibility() {
    if (!this.flickity) return
    const { columns, columnsMb } = this.carouselSettings
    const hideArrows = shouldHideCarouselArrows(this.flickity.cells.length, columns, columnsMb)
    this.classList.toggle('carousel-arrows-hidden', hideArrows)
  }

  isFlickityElement(element) {
    if (!element.classList) return false

    return Array.from(element.classList).some((className) => className.startsWith('flickity-'))
  }

  setupMutationObserver() {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect()
    }

    this.mutationObserver = new MutationObserver((mutations) => {
      // Flickity's own activate/deactivate move the cells between this element
      // and `.flickity-slider`. Those moves must never be read as external
      // content changes, or destroy → re-init → destroy loops forever.
      if (this.isReinitializing) return

      let shouldReinitialize = false

      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          const hasRelevantChanges =
            Array.from(mutation.addedNodes).some(
              (node) => node.nodeType === Node.ELEMENT_NODE && node.tagName !== 'STYLE' && !this.isFlickityElement(node)
            ) ||
            Array.from(mutation.removedNodes).some(
              (node) => node.nodeType === Node.ELEMENT_NODE && node.tagName !== 'STYLE' && !this.isFlickityElement(node)
            )

          if (hasRelevantChanges) {
            shouldReinitialize = true
          }
        }
      })

      if (shouldReinitialize) {
        this.reinitializeCarousel()
      }
    })

    this.observeChildren()
  }

  observeChildren() {
    this.mutationObserver?.observe(this, {
      childList: true,
      subtree: false
    })
  }

  reinitializeCarousel() {
    if (this.isReinitializing) return
    this.isReinitializing = true

    // Stop recording before we touch the DOM. destroy() re-appends every cell
    // to this element and the next init moves them back into `.flickity-slider`;
    // with the observer still attached both are recorded as relevant changes and
    // schedule another reinit, one full teardown+rebuild per frame, forever.
    this.mutationObserver?.disconnect()

    if (this.flickity) {
      this.flickity.destroy()
      this.flickity = null
    }

    this.reinitFrame = requestAnimationFrame(() => {
      this.reinitFrame = null
      this.initializeCarousel()
      this.isReinitializing = false
      this.observeChildren()
    })
  }

  observedAttributes = ['carousel-settings']

  get carouselSettings() {
    const carouselSettings = this.getAttribute('carousel-settings')
    if (!carouselSettings) return {}
    const parsedSettings = parseJSON(carouselSettings)
    if (parsedSettings?.groupCells !== undefined) {
      const value = parsedSettings.groupCells

      if (value === 'true') {
        parsedSettings.groupCells = true
      } else if (value === 'false') {
        parsedSettings.groupCells = false
      } else if (!isNaN(value) && typeof value === 'string') {
        parsedSettings.groupCells = Number(value)
      }
    }
    return parsedSettings
  }

  disconnectedCallback() {
    if (this.reinitFrame) {
      cancelAnimationFrame(this.reinitFrame)
      this.reinitFrame = null
    }
    if (this.teardownFrame) {
      cancelAnimationFrame(this.teardownFrame)
      this.teardownFrame = null
    }
    this.isReinitializing = false

    this.initObserver?.disconnect()
    this.initObserver = null
    this.teardownObserver?.disconnect()
    this.teardownObserver = null

    if (this.mutationObserver) {
      this.mutationObserver.disconnect()
      this.mutationObserver = null
    }

    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize)
      this.handleResize = null
    }

    if (this.flickity) {
      this.flickity.destroy()
      this.flickity = null
    }
  }
}
